import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Shield, Info } from 'lucide-react';

export const CookieConsentBanner: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Check if user has already consented
    const hasConsent = localStorage.getItem('cookieConsent');
    
    // Only show banner if no consent has been given
    if (!hasConsent) {
      // Slight delay to avoid immediate popup on page load
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1500);
      
      return () => clearTimeout(timer);
    }
  }, []);

  const acceptAll = () => {
    try {
      localStorage.setItem('cookieConsent', 'all');
      setIsVisible(false);
      
      // If Cookiebot is available, trigger acceptance
      if (window.Cookiebot) {
        window.Cookiebot.submitCustomConsent(true, true, true);
      }
    } catch (error) {
      console.error('Error saving cookie consent:', error);
    }
  };

  const acceptEssential = () => {
    try {
      localStorage.setItem('cookieConsent', 'essential');
      setIsVisible(false);
      
      // If Cookiebot is available, trigger essential-only acceptance
      if (window.Cookiebot) {
        window.Cookiebot.submitCustomConsent(true, false, false);
      }
    } catch (error) {
      console.error('Error saving cookie consent:', error);
    }
  };

  const toggleDetails = () => {
    setShowDetails(prev => !prev);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 50 }}
          className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-white dark:bg-gray-800 shadow-lg border-t border-gray-200 dark:border-gray-700"
        >
          <div className="container mx-auto max-w-4xl">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center">
                <Shield className="text-indigo-600 dark:text-indigo-400 mr-2" size={24} />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Cookie Consent</h3>
              </div>
              <button 
                onClick={() => setIsVisible(false)} 
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
            
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. 
              By clicking "Accept All", you consent to our use of cookies.
            </p>
            
            <div className="flex flex-wrap gap-3 mb-3">
              <button
                onClick={acceptAll}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md text-sm font-medium transition-colors"
              >
                Accept All
              </button>
              <button
                onClick={acceptEssential}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-md text-sm font-medium transition-colors"
              >
                Essential Only
              </button>
              <button
                onClick={toggleDetails}
                className="px-4 py-2 flex items-center bg-transparent hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-md text-sm font-medium transition-colors"
              >
                <Info size={16} className="mr-1" />
                {showDetails ? 'Hide Details' : 'Cookie Details'}
              </button>
            </div>
            
            <AnimatePresence>
              {showDetails && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-md mb-4">
                    <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Cookie Types</h4>
                    <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                      <li className="flex items-start">
                        <span className="font-medium mr-2">Essential:</span> 
                        <span>Required for basic website functionality. Cannot be disabled.</span>
                      </li>
                      <li className="flex items-start">
                        <span className="font-medium mr-2">Analytics:</span> 
                        <span>Help us understand how visitors interact with our website.</span>
                      </li>
                      <li className="flex items-start">
                        <span className="font-medium mr-2">Marketing:</span> 
                        <span>Used to track visitors across websites to display relevant advertisements.</span>
                      </li>
                    </ul>
                    <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
                      For more information, please view our <a href="/privacy-policy" className="text-indigo-600 dark:text-indigo-400 hover:underline">Privacy Policy</a>.
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Add TypeScript interface for Cookiebot
declare global {
  interface Window {
    Cookiebot?: {
      submitCustomConsent: (essential: boolean, preferences: boolean, statistics: boolean) => void;
    };
  }
}